import Typography from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import { I18nNamespace } from 'constants/i18n.const';
import {
  INIT_BACKGROUND_CATEGORY_ID,
  INIT_BACKGROUND_ID,
  ITEM_CATEGORY_SELECTABLE_PER_PAGE,
  ITEM_SELECTABLE_PER_PAGE,
  SHOOTING_TYPES,
  VERTICAL_SCREEN_ITEM_SELECTABLE_PER_PAGE,
  VERTICAL_SCREEN_VERTICAL_FRAME_ITEM_SELECTABLE_PER_PAGE,
} from 'constants/photo.const';
import { useBoothAppContext } from 'context/BoothAppContext';
import { useSoundContext } from 'context/SoundContext';
import { isHigher } from 'helpers/math.helper';
import { isEqualVal } from 'helpers/string.helper';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setSelfeAppStateAction } from 'store/features/app/selfeAppSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { useModal } from '../../../hooks/useModal';
import EditPhotoGridSelectItem from '../edit-photo/EditPhotoGridSelectItem';
import EditPhotoSelectCategory from '../edit-photo/EditPhotoSelectCategory';
import EditPhotoSheetPreview from '../edit-photo/EditPhotoSheetPreview';
import './select-background.css';

function SelectBackground() {
  const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.PAGE]);
  const dispatch = useAppDispatch();
  const { audio } = useSoundContext();
  const { listBackgroundCategoryPhotos, currentLayout, listBackgroundPhotos } =
    useBoothAppContext();
  const { backgroundId, backgroundCategoryId, shootingModeId } = useAppSelector(
    (state) => state.selfeApp,
  );
  const { isVerticalScreen } = useAppSelector((s) => s.booth);
  const [isInBackgroundScreen, setIsInBackgroundScreen] = useState(false);
  const isHasVideo =
    !shootingModeId || isEqualVal(shootingModeId, SHOOTING_TYPES.COUNTDOWN);

  const { openConfirmModal, closeConfirmModal } = useModal();

  const handleSelectBackground = (id: number) => {
    audio?.playClickToSelect?.();
    setIsInBackgroundScreen(true);
    if (id === backgroundId) {
      dispatch(setSelfeAppStateAction({ backgroundId: INIT_BACKGROUND_ID }));
    } else {
      dispatch(setSelfeAppStateAction({ backgroundId: id }));
    }
  };

  const handleSelectCategory = (category: number | undefined) => {
    if (category) {
      audio?.playClickToSelect?.();
      dispatch(setSelfeAppStateAction({ backgroundCategoryId: category }));
    }
  };

  useEffect(() => {
    !backgroundCategoryId &&
      dispatch(
        setSelfeAppStateAction({
          backgroundCategoryId:
            listBackgroundCategoryPhotos?.data?.[0]?.id ||
            INIT_BACKGROUND_CATEGORY_ID,
        }),
      );
  }, [dispatch, listBackgroundCategoryPhotos, backgroundCategoryId]);

  useEffect(() => {
    if (!backgroundId && !isInBackgroundScreen) {
      openConfirmModal({
        RenderContent: () => (
          <div>
            <Typography variant={TYPOGRAPHY_VARIANTS.H2}>{`${t(
              'common:warningBackgroundTitle',
            )}`}</Typography>
            <br />
            <Typography variant={TYPOGRAPHY_VARIANTS.BODY}>{`${t(
              'common:warningBackground',
            )}`}</Typography>
          </div>
        ),
        NextButtonProps: { children: t('common:confirm') },
        hidePrev: true,
        isUseTimer: true,
        isTimerInBtnNext: true,
        onOk: () => {
          closeConfirmModal?.();
        },
        onTimeUp: () => {
          closeConfirmModal();
        },
        onClose: () => {
          closeConfirmModal();
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundId, isInBackgroundScreen]);

  React.useEffect(() => {
    dispatch(
      setSelfeAppStateAction({
        backgroundId: backgroundId || INIT_BACKGROUND_ID,
      }),
    );
  }, [dispatch, backgroundId]);

  const isVerticalFrame = useMemo(
    () => isHigher(currentLayout?.height, currentLayout?.width),
    [currentLayout],
  );

  // Số theme item trên 1 page
  const backgroundPerPage = useMemo(() => {
    const verticalItemPerPage = isVerticalFrame
      ? VERTICAL_SCREEN_VERTICAL_FRAME_ITEM_SELECTABLE_PER_PAGE
      : VERTICAL_SCREEN_ITEM_SELECTABLE_PER_PAGE;

    return isVerticalScreen ? verticalItemPerPage : ITEM_SELECTABLE_PER_PAGE;
  }, [isVerticalScreen, isVerticalFrame]);

  return (
    <>
      <div className="background-page edit-page">
        <EditPhotoSelectCategory
          selectedCatId={backgroundCategoryId}
          listCatData={listBackgroundCategoryPhotos}
          catPerPage={ITEM_CATEGORY_SELECTABLE_PER_PAGE}
          onSelectCategory={handleSelectCategory}
        />
        <div className="edit-page__selection-section">
          <EditPhotoSheetPreview isShowWarningNotShowingOnVideo={isHasVideo} />
          <EditPhotoGridSelectItem
            selectedId={backgroundId}
            listData={listBackgroundPhotos}
            itemPerPage={backgroundPerPage}
            onSelectItem={handleSelectBackground}
          />
        </div>
      </div>
    </>
  );
}

export default SelectBackground;
